import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUser, FiMail, FiLock, FiSave, FiArrowLeft,
  FiShield, FiEye, FiEyeOff, FiCheck, FiEdit2
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { authService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Perfil.css';

const MODULOS_LABELS = {
  dashboard:     'Dashboard',
  colaboradores: 'Colaboradores',
  reunioes:      'Reuniões',
  presencas:     'Presenças',
  irmandade:     'Irmandade',
  visitas:       'Visitas',
  cargos:        'Cargos',
  congregacoes:  'Congregações',
  relatorios:    'Relatórios',
  usuarios:      'Usuários',
};

const Perfil = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const [tabAtiva, setTabAtiva] = useState('dados');
  const [loading, setLoading] = useState(false);

  const [dadosForm, setDadosForm] = useState({
    nome: user?.nome || '',
    email: user?.email || '',
  });

  const [senhaForm, setSenhaForm] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: '',
  });

  const [mostrarSenha, setMostrarSenha] = useState({
    atual: false,
    nova: false,
    confirmar: false,
  });

  const modulos = user?.permissoes_modulos || user?.permissoesModulos || {};
  const isAdmin = user?.permissao === 'admin';

  const getPermissaoLabel = (perm) => {
    const labels = { admin: 'Administrador', editor: 'Editor', viewer: 'Visualizador' };
    return labels[perm] || perm;
  };

  const handleDadosChange = (e) => {
    const { name, value } = e.target;
    setDadosForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSenhaChange = (e) => {
    const { name, value } = e.target;
    setSenhaForm(prev => ({ ...prev, [name]: value }));
  };

  const salvarDados = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authService.updateProfile({
        nome: dadosForm.nome,
        email: dadosForm.email,
      });
      if (response.success) {
        updateUser(response.data || { nome: dadosForm.nome, email: dadosForm.email });
        toast.success('Perfil atualizado com sucesso!');
      } else {
        toast.error(response.message || 'Erro ao atualizar perfil');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const alterarSenha = async (e) => {
    e.preventDefault();
    if (senhaForm.novaSenha !== senhaForm.confirmarSenha) {
      toast.error('Nova senha e confirmação não conferem');
      return;
    }
    if (senhaForm.novaSenha.length < 6) {
      toast.error('A nova senha deve ter ao menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      const response = await authService.changePassword({
        senhaAtual: senhaForm.senhaAtual,
        novaSenha: senhaForm.novaSenha,
      });
      if (response.success) {
        setSenhaForm({ senhaAtual: '', novaSenha: '', confirmarSenha: '' });
        toast.success('Senha alterada com sucesso!');
      } else {
        toast.error(response.message || 'Erro ao alterar senha');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Senha atual incorreta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="perfil-page">
      <div className="perfil-header">
        <button className="btn-voltar" onClick={() => navigate(-1)}>
          <FiArrowLeft /> Voltar
        </button>
        <div className="perfil-titulo">
          <h1><FiUser /> Meu Perfil</h1>
          <p>Gerencie suas informações e segurança</p>
        </div>
      </div>

      <div className="perfil-layout">
        {/* Sidebar do perfil */}
        <div className="perfil-sidebar">
          <div className="perfil-avatar-box">
            <div className="perfil-avatar">
              <FiUser />
            </div>
            <h3>{user?.nome}</h3>
            <span className={`role-badge role-${user?.permissao}`}>
              <FiShield /> {getPermissaoLabel(user?.permissao)}
            </span>
            <p className="perfil-email">{user?.email}</p>
          </div>

          <nav className="perfil-nav">
            <button
              className={`perfil-nav-item ${tabAtiva === 'dados' ? 'active' : ''}`}
              onClick={() => setTabAtiva('dados')}
            >
              <FiEdit2 /> Dados Pessoais
            </button>
            <button
              className={`perfil-nav-item ${tabAtiva === 'senha' ? 'active' : ''}`}
              onClick={() => setTabAtiva('senha')}
            >
              <FiLock /> Alterar Senha
            </button>
            <button
              className={`perfil-nav-item ${tabAtiva === 'permissoes' ? 'active' : ''}`}
              onClick={() => setTabAtiva('permissoes')}
            >
              <FiShield /> Minhas Permissões
            </button>
          </nav>
        </div>

        {/* Conteúdo principal */}
        <div className="perfil-content">
          {/* Aba: Dados Pessoais */}
          {tabAtiva === 'dados' && (
            <div className="perfil-card">
              <div className="card-header">
                <FiEdit2 />
                <h2>Dados Pessoais</h2>
              </div>
              <form onSubmit={salvarDados} className="perfil-form">
                <div className="form-group">
                  <label><FiUser /> Nome completo</label>
                  <input
                    type="text"
                    name="nome"
                    value={dadosForm.nome}
                    onChange={handleDadosChange}
                    required
                    placeholder="Seu nome completo"
                  />
                </div>
                <div className="form-group">
                  <label><FiMail /> E-mail</label>
                  <input
                    type="email"
                    name="email"
                    value={dadosForm.email}
                    onChange={handleDadosChange}
                    required
                    placeholder="seu@email.com"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-salvar" disabled={loading}>
                    <FiSave /> {loading ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Aba: Alterar Senha */}
          {tabAtiva === 'senha' && (
            <div className="perfil-card">
              <div className="card-header">
                <FiLock />
                <h2>Alterar Senha</h2>
              </div>
              <form onSubmit={alterarSenha} className="perfil-form">
                <div className="form-group">
                  <label><FiLock /> Senha atual</label>
                  <div className="input-password">
                    <input
                      type={mostrarSenha.atual ? 'text' : 'password'}
                      name="senhaAtual"
                      value={senhaForm.senhaAtual}
                      onChange={handleSenhaChange}
                      required
                      placeholder="Digite sua senha atual"
                    />
                    <button
                      type="button"
                      className="toggle-senha"
                      onClick={() => setMostrarSenha(s => ({ ...s, atual: !s.atual }))}
                    >
                      {mostrarSenha.atual ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label><FiLock /> Nova senha</label>
                  <div className="input-password">
                    <input
                      type={mostrarSenha.nova ? 'text' : 'password'}
                      name="novaSenha"
                      value={senhaForm.novaSenha}
                      onChange={handleSenhaChange}
                      required
                      placeholder="Mínimo 6 caracteres"
                    />
                    <button
                      type="button"
                      className="toggle-senha"
                      onClick={() => setMostrarSenha(s => ({ ...s, nova: !s.nova }))}
                    >
                      {mostrarSenha.nova ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label><FiLock /> Confirmar nova senha</label>
                  <div className="input-password">
                    <input
                      type={mostrarSenha.confirmar ? 'text' : 'password'}
                      name="confirmarSenha"
                      value={senhaForm.confirmarSenha}
                      onChange={handleSenhaChange}
                      required
                      placeholder="Repita a nova senha"
                    />
                    <button
                      type="button"
                      className="toggle-senha"
                      onClick={() => setMostrarSenha(s => ({ ...s, confirmar: !s.confirmar }))}
                    >
                      {mostrarSenha.confirmar ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  {senhaForm.novaSenha && senhaForm.confirmarSenha && (
                    <span className={`senha-match ${senhaForm.novaSenha === senhaForm.confirmarSenha ? 'ok' : 'err'}`}>
                      {senhaForm.novaSenha === senhaForm.confirmarSenha ? '✓ Senhas conferem' : '✗ Senhas não conferem'}
                    </span>
                  )}
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-salvar" disabled={loading}>
                    <FiSave /> {loading ? 'Salvando...' : 'Alterar Senha'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Aba: Permissões */}
          {tabAtiva === 'permissoes' && (
            <div className="perfil-card">
              <div className="card-header">
                <FiShield />
                <h2>Minhas Permissões</h2>
              </div>
              <div className="permissoes-info">
                <p className="permissao-role">
                  Seu perfil: <strong className={`role-badge role-${user?.permissao}`}>{getPermissaoLabel(user?.permissao)}</strong>
                </p>
                {isAdmin ? (
                  <p className="admin-note">
                    <FiCheck /> Como Administrador, você tem acesso total a todos os módulos.
                  </p>
                ) : (
                  <div className="modulos-grid">
                    {Object.entries(MODULOS_LABELS).map(([key, label]) => {
                      const mod = isAdmin
                        ? { read: true, write: true }
                        : (modulos[key] || { read: false, write: false });
                      return (
                        <div key={key} className="modulo-row">
                          <span className="modulo-nome">{label}</span>
                          <span className={`perm-chip ${mod.read ? 'on' : 'off'}`}>
                            {mod.read ? <FiCheck /> : '—'} Visualizar
                          </span>
                          <span className={`perm-chip ${mod.write ? 'on' : 'off'}`}>
                            {mod.write ? <FiCheck /> : '—'} Editar
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Perfil;
